import { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { Link } from "react-router-dom";
import { Auth } from "aws-amplify";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router";
import {
  Box,
  InputLeftElement,
  InputRightElement,
} from "@chakra-ui/react";
import { Button } from "../../Components/ui/button";
import { Label } from "../../Components/ui/label";
import { FourDots } from "../../Components/FourDots";
import { AuthFooter } from "../../Components/AuthFooter";

import { MESSAGE, MinMaxMessage } from "../../Constants/Message";
import { Eye, Password, SercurityIcon } from "../../Assets/SvgIcons";
import { InputV2 } from "../../Reusable";

import * as Sentry from "@sentry/react";
import { AuthCognitoPool, configureAmplifyPool } from "../../utils/amplifyPools";

// Password Requirement Component
function PasswordRequirement({ met, text }: { met: boolean; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-4 h-4 rounded-full flex items-center justify-center ${
          met ? "bg-green-500" : "bg-gray-300"
        }`}
      >
        {met && (
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
      <span className={met ? "text-green-700" : "text-gray-600"}>
        {text}
      </span>
    </div>
  );
}

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const location = useLocation();
  //@ts-ignore
  const { username, cognitoPool: statePool } = location.state ? location.state : {};
  const cognitoPool: AuthCognitoPool = statePool === "measureOperator" ? "measureOperator" : "member";
  const [loading, setLoading] = useState(false);
  let navigate = useNavigate();

  useEffect(() => {
    try {
      configureAmplifyPool(cognitoPool);
    } catch (e) {
      console.error(e);
    }
  }, [cognitoPool]);
  
  //form-validation
  const regex_NoWhiteSpace = /^\S*$/;
  const regex_Password = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,30}$/;
  
  const validationSchema = Yup.object({
    code: Yup.string()
      .required(MESSAGE.INPUT_REQUIRED)
      .matches(regex_NoWhiteSpace, {
        message: MESSAGE.WHITE_SPACE,
        excludeEmptyString: true,
      }),
    password: Yup.string()
      .required(MESSAGE.INPUT_REQUIRED)
      .matches(regex_Password, {
        message: "Please meet the password requirements",
        excludeEmptyString: true,
      }),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required(MESSAGE.INPUT_REQUIRED),
  });

  const forgotPasswordSubmit = async (values) => {
    //@ts-ignore
    const { username } = location.state ? location.state : "";
    const code = values.code.trim();
    const password = values.password.trim();
    
    // Validate password requirements before submitting
    const passwordChecks = {
      minLength: password.length >= 8,
      maxLength: password.length <= 30,
      hasNumber: /\d/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password),
    };

    const allPasswordRequirementsMet =
      Object.values(passwordChecks).every(Boolean);

    if (!allPasswordRequirementsMet) {
      toast.error("Please meet all password requirements");
      return;
    }

    try {
      setLoading(true);
      await Auth.forgotPasswordSubmit(username, code, password);
      toast.success("Password has been changed");
      navigate(cognitoPool === "measureOperator" ? "/measure/login" : "/login");
    } catch (err: any) {
      Sentry.captureException(err);
      setLoading(false);
      console.error(err);
      
      // Show specific error messages
      if (err.code === "CodeMismatchException") {
        toast.error("Invalid reset code. Please check your email and try again.");
      } else if (err.code === "ExpiredCodeException") {
        toast.error("The reset code has expired. Please request a new one.");
      } else if (err.code === "InvalidPasswordException") {
        toast.error("Password does not meet requirements. Please check all requirements.");
      } else {
        toast.error("Failed to reset password. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <Link to="/">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-black lowercase">
                  measure
                </span>
                <span className="text-lg font-semibold text-red-600 uppercase">
                  IGNITE
                </span>
              </div>
            </Link>
          </div>

          {/* Instruction text */}
          <p className="text-gray-700 mb-8 text-center">
            Please enter the security code sent to the email you signed up with
            and your new password.
          </p>

          <Formik
            initialValues={{
              code: "",
              password: "",
              confirmPassword: "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (data, handlers) => {
              handlers.setSubmitting(true);
              handlers.validateForm(data);
              await forgotPasswordSubmit(data);
              handlers.setSubmitting(false);
            }}
          >
            {(formik) => {
              // Real-time password validation checks
              const passwordValue = formik.values.password || "";
              const passwordChecks = {
                minLength: passwordValue.length >= 8,
                maxLength: passwordValue.length <= 30,
                hasNumber: /\d/.test(passwordValue),
                hasUppercase: /[A-Z]/.test(passwordValue),
                hasLowercase: /[a-z]/.test(passwordValue),
                // AWS Cognito requires at least one symbol character from: @$!%*?&
                hasSpecial: /[@$!%*?&]/.test(passwordValue),
              };

              const allPasswordRequirementsMet =
                Object.values(passwordChecks).every(Boolean);

              return (
                <Form className="space-y-6">
                  {/* Security Code */}
                  <div className="space-y-2">
                    <Label htmlFor="code">Security code</Label>
                    <Box>
                      <InputV2
                        name="code"
                        placeholder="Security code"
                        inputLeftElement={
                          <InputLeftElement
                            pointerEvents="none"
                            children={<SercurityIcon />}
                          />
                        }
                        cypressInputSelector="cypress-security-code"
                        cypressErrorSelector="cypress-security-code-error"
                      />
                    </Box>
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Box>
                      <InputV2
                        name="password"
                        placeholder="Password"
                        inputLeftElement={
                          <InputLeftElement
                            pointerEvents="none"
                            children={<Password />}
                          />
                        }
                        inputRightElement={
                          <InputRightElement
                            children={<Eye />}
                            pointerEvents="initial"
                            className="cursor-pointer"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        }
                        restInput={{ 
                          type: showPassword ? "text" : "password",
                          onFocus: () => setPasswordFocused(true),
                          maxLength: 30
                        }}
                        cypressInputSelector="cypress-new-password"
                        cypressErrorSelector="cypress-new-password-error"
                      />
                    </Box>

                    {/* Password Requirements - Show when focused or has value */}
                    {(passwordFocused || passwordValue) && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-1.5 text-xs">
                        <PasswordRequirement
                          met={passwordChecks.minLength}
                          text="Minimum 8 characters"
                        />
                        <PasswordRequirement
                          met={passwordChecks.maxLength}
                          text="30 max"
                        />
                        <PasswordRequirement
                          met={passwordChecks.hasNumber}
                          text="1 number"
                        />
                        <PasswordRequirement
                          met={passwordChecks.hasUppercase}
                          text="1 uppercase letter"
                        />
                        <PasswordRequirement
                          met={passwordChecks.hasLowercase}
                          text="1 lowercase letter"
                        />
                        <PasswordRequirement
                          met={passwordChecks.hasSpecial}
                          text="1 special character (@ $ ! % * ? &)"
                        />
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Box>
                      <InputV2
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        inputLeftElement={
                          <InputLeftElement
                            pointerEvents="none"
                            children={<Password />}
                          />
                        }
                        inputRightElement={
                          <InputRightElement
                            children={<Eye />}
                            pointerEvents="initial"
                            className="cursor-pointer"
                            onClick={() =>
                              setShowPasswordConfirm(!showPasswordConfirm)
                            }
                          />
                        }
                        restInput={{
                          type: showPasswordConfirm ? "text" : "password",
                        }}
                        cypressInputSelector="cypress-new-password-confirmation"
                        cypressErrorSelector="cypress-new-password-confirmation-error"
                      />
                    </Box>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={loading || !allPasswordRequirementsMet}
                  >
                    {loading ? "Resetting password..." : "Reset password"}
                  </Button>
                </Form>
              );
            }}
          </Formik>

          {/* Back to Login Link */}
          <p className="mt-6 text-center text-sm text-gray-600">
            Go back to{" "}
            <Link
              to="/login"
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              Login?
            </Link>
          </p>

          <AuthFooter />
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-center items-center">
          <div className="max-w-lg text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              Reset your password
            </h2>
            <p className="text-gray-700">
              Enter the security code from your email and create a new password
              to regain access to your account.
            </p>
          </div>
          <FourDots size="2xl" opacity={0.7} />
        </div>
      </div>
    </div>
  );
};
export default ResetPassword;


